import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useIdentity } from "../../auth/useIdentity";
import { shortName } from "../../auth/scope";
import { useProviders } from "./useProviders";
import { flagEnabled } from "../../config";
import { ADD_PROVIDER_PATH, canManageSubOperators } from "../../components/nav";
import { kindLabel, labelOf, listSubOperators, setSubOperatorSuspended, type SubOperator } from "../../api/suboperators";
import { inviteSMSNote, inviteSubOperatorOwner, listOperatorLogins, type InviteInput, type OperatorLogin } from "../../api/operatorLogins";
import { InviteDialog, statusChip } from "../staff/Staff";

/**
 * Every provider beneath the signed-in operator, searchable, each with the
 * one action that matters: open its console. The sidebar shows the first six;
 * this is the whole list, for the 20–100+ case the brief asks about.
 */
export function ProvidersScreen() {
  const identity = useIdentity();
  const navigate = useNavigate();
  const { all, isLoading, error } = useProviders();
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const rows = all.filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.namespace.toLowerCase().includes(needle));
  const rootName = identity.rootNamespace ? shortName(identity.rootNamespace) : "";

  // The providers this namespace ADDED (owner, 2026-09-19): their kind, and a
  // suspend/resume the gateway allows only on those -- never on one created
  // by sign-up, never on itself.
  const parent = identity.namespace ?? "";
  const manage = flagEnabled("providers.manage") && canManageSubOperators(identity.roles);
  const qc = useQueryClient();
  const subs = useQuery({
    queryKey: ["suboperators", parent],
    enabled: manage && Boolean(identity.token && parent),
    queryFn: ({ signal }) => listSubOperators(identity, parent, signal),
    retry: false,
  });
  const added = new Map<string, SubOperator>((subs.data ?? []).map((s) => [s.namespace, s]));
  // The owner login of each sub-operator it added (owner, 2026-10-18): one
  // invitation per sub-operator, accepted at /invite with the owner's own mobile.
  const ownerLogins = flagEnabled("operator.logins") && manage;
  const owners = useQuery({
    queryKey: ["operatorlogins", parent, "suboperator"],
    enabled: ownerLogins && Boolean(identity.token && parent),
    queryFn: ({ signal }) => listOperatorLogins(identity, parent, "suboperator", signal),
    retry: false,
  });
  const ownerOf = new Map<string, OperatorLogin>();
  for (const l of owners.data ?? []) {
    // Newest first from the server: keep the first live one per namespace.
    if (!ownerOf.has(l.namespace) && ["PENDING", "ACTIVE", "DISABLED"].includes(l.status)) ownerOf.set(l.namespace, l);
  }
  const [inviteFor, setInviteFor] = useState<SubOperator | null>(null);
  const [ownerNotice, setOwnerNotice] = useState("");
  const inviteOwner = useMutation({
    mutationFn: (v: { sub: SubOperator; input: InviteInput }) => inviteSubOperatorOwner(identity, parent, labelOf(v.sub.namespace), v.input),
    onSuccess: async (l) => {
      await qc.invalidateQueries({ queryKey: ["operatorlogins", parent, "suboperator"] });
      setInviteFor(null);
      setOwnerNotice(`Invitation recorded for ${l.firstname} ${l.lastname} as owner of ${shortName(l.namespace)}. ${inviteSMSNote(l)} They open ankhos.io/signin, choose "Accept your invitation" and verify ${l.mobile}.`);
    },
  });
  const [confirm, setConfirm] = useState<{ sub: SubOperator; suspend: boolean } | null>(null);
  const [reason, setReason] = useState("");
  const toggle = useMutation({
    mutationFn: (c: { sub: SubOperator; suspend: boolean }) => setSubOperatorSuspended(identity, parent, labelOf(c.sub.namespace), c.suspend, reason),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["suboperators", parent] }), qc.invalidateQueries({ queryKey: ["providers"] })]);
      setConfirm(null);
      setReason("");
    },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" component="h1">
            Providers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {all.length} under {rootName} · <code>{identity.rootNamespace}</code>
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <TextField size="small" label="Filter providers" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
        {manage && (
          <Button variant="contained" component={RouterLink} to={ADD_PROVIDER_PATH}>
            + Add New Provider
          </Button>
        )}
      </Box>
      {ownerNotice && <Alert severity="success" onClose={() => setOwnerNotice("")}>{ownerNotice}</Alert>}
      {error ? (
        <Alert severity="warning">Could not list your providers: {error instanceof Error ? error.message : String(error)}</Alert>
      ) : !isLoading && all.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No provider is set up beneath your namespace yet.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Namespace</TableCell>
                <TableCell>Under</TableCell>
                {manage && <TableCell>Kind</TableCell>}
                {ownerLogins && <TableCell>Owner login</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.namespace} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{p.namespace}</TableCell>
                  <TableCell>{shortName(p.namespace.slice(0, p.namespace.lastIndexOf(".")))}</TableCell>
                  {manage && <TableCell>{added.has(p.namespace) ? kindLabel(added.get(p.namespace)!.kind) : "—"}</TableCell>}
                  {ownerLogins && (
                    <TableCell>
                      {ownerOf.has(p.namespace) ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {statusChip(ownerOf.get(p.namespace)!)}
                          <Typography variant="caption">{ownerOf.get(p.namespace)!.firstname} {ownerOf.get(p.namespace)!.lastname}</Typography>
                        </Box>
                      ) : added.has(p.namespace) ? (
                        <Button size="small" onClick={() => { inviteOwner.reset(); setInviteFor(added.get(p.namespace)!); }}>
                          Invite owner
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {added.get(p.namespace)?.suspended ? (
                      <Chip size="small" label="Suspended" color="warning" variant="outlined" title={added.get(p.namespace)?.suspensionreason ?? ""} />
                    ) : (
                      <Chip size="small" label={p.active ? "Active" : "Inactive"} color={p.active ? "success" : "default"} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {manage && added.has(p.namespace) && (
                      <Button
                        size="small"
                        color={added.get(p.namespace)!.suspended ? "primary" : "warning"}
                        sx={{ mr: 1 }}
                        onClick={() => setConfirm({ sub: added.get(p.namespace)!, suspend: !added.get(p.namespace)!.suspended })}
                      >
                        {added.get(p.namespace)!.suspended ? "Resume" : "Suspend"}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        identity.enter(p.namespace);
                        navigate("/");
                      }}
                    >
                      Open console
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {inviteFor && (
        <InviteDialog
          open
          title={`Invite the owner of ${shortName(inviteFor.namespace)}`}
          intro="They get their own login for this provider, as its owner. They manage its plans, subscribers and staff; you keep your view from above."
          withRoles={false}
          pending={inviteOwner.isPending}
          error={inviteOwner.error}
          onClose={() => setInviteFor(null)}
          onSubmit={(input) => inviteOwner.mutate({ sub: inviteFor, input })}
        />
      )}
      <Dialog open={confirm !== null} onClose={() => !toggle.isPending && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {confirm?.suspend ? "Suspend" : "Resume"} {confirm ? shortName(confirm.sub.namespace) : ""}?
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2">
            {confirm?.suspend
              ? "A suspended provider can raise no invoice until you resume it. Its sign-in and its subscribers' service are not stopped by this yet."
              : "The provider can invoice again."}
          </Typography>
          {confirm?.suspend && <TextField label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 255 }} />}
          {toggle.error && <Alert severity="error">{toggle.error instanceof Error ? toggle.error.message : String(toggle.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={toggle.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color={confirm?.suspend ? "warning" : "primary"} disabled={toggle.isPending} onClick={() => confirm && toggle.mutate(confirm)}>
            {confirm?.suspend ? "Suspend" : "Resume"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
